import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, PersonSimpleWalk, PersonSimpleRun, Lightning, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";
import axios from "axios";
import { useAuth, API } from "../App";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Button } from "../components/ui/button";

const Onboarding = () => {
  const location = useLocation();
  const initialGoal = location.state?.goal || "";
  const initialIntensity = location.state?.intensity || "";
  
  const [step, setStep] = useState(initialIntensity ? 2 : 1); // Skip intensity if already selected
  const [goal, setGoal] = useState(initialGoal);
  const [intensity, setIntensity] = useState(initialIntensity);
  const [activityLevel, setActivityLevel] = useState("");
  const [weight, setWeight] = useState("");
  const [targetWeight, setTargetWeight] = useState("");
  const [height, setHeight] = useState("");
  const [age, setAge] = useState("");
  const [gender, setGender] = useState("");
  const [showWarning, setShowWarning] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const navigate = useNavigate();
  const { updateUser } = useAuth();

  // If goal came from FocusSelection, start at step 1 (intensity) or 2 (activity) if intensity provided
  useEffect(() => {
    if (initialGoal) {
      setGoal(initialGoal);
      // Para "Manter Saúde", pula a intensidade automaticamente
      if (initialGoal === 'manter') {
        setIntensity('moderado');
        setStep(2);
      }
    }
    if (initialIntensity) {
      setIntensity(initialIntensity);
      setStep(2); // Skip to activity level
    }
  }, [initialGoal, initialIntensity]);

  const goalLabels = {
    perder: "Perder Peso 🔥",
    manter: "Manter Saúde ⚖️",
    ganhar: "Ganhar Peso 💪"
  };

  const intensities = [
    { id: "saudavel", label: "Saudável", desc: "Mudanças graduais e sustentáveis" },
    { id: "moderado", label: "Moderado", desc: "Equilíbrio entre velocidade e saúde" },
    { id: "agressivo", label: "Agressivo", desc: "Resultados rápidos, mais rigor" }
  ];

  const activities = [
    { id: "sedentario", label: "Sedentário", desc: "Trabalho sentado, pouco movimento", icon: PersonSimpleWalk },
    { id: "ativo", label: "Ativo(a)", desc: "Caminhadas ou 3 treinos por semana", icon: PersonSimpleRun },
    { id: "intenso", label: "Intenso(a)", desc: "Trabalho braçal ou treinos diários", icon: Lightning }
  ];

  const handleIntensitySelect = (selectedIntensity) => {
    if (selectedIntensity === "agressivo") {
      setShowWarning(true);
    } else {
      setIntensity(selectedIntensity);
      setStep(2);
    }
  };

  const confirmAggressive = () => {
    setIntensity("agressivo");
    setShowWarning(false);
    setStep(2);
  };

  const handleActivitySelect = (selectedActivity) => {
    setActivityLevel(selectedActivity);
    setStep(3);
  };

  const handleSubmit = async () => {
    if (!weight || !height || !age || !gender) {
      toast.error("Por favor, preenche todos os campos");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.put(`${API}/users/onboarding`, {
        goal,
        intensity,
        activity_level: activityLevel,
        current_weight: parseFloat(weight),
        target_weight: parseFloat(targetWeight || weight),
        height: parseFloat(height),
        age: parseInt(age),
        gender
      });

      updateUser({ 
        goal,
        daily_calories: response.data.daily_calories
      });
      
      // Build success message with projection
      let successMsg = `Perfil configurado! Meta: ${response.data.daily_calories} kcal/dia`;
      if (response.data.weeks_to_goal > 0) {
        successMsg = `🎯 ${response.data.projection_text}! Meta diária: ${response.data.daily_calories} kcal`;
      }
      
      toast.success(successMsg);
      navigate("/");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao salvar perfil");
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (step === 1) {
      // Passo 1 (Intensidade) -> Voltar para seleção de foco
      navigate('/focus');
    } else if (step === 2) {
      // Passo 2 (Nível de Movimento)
      // Se o objetivo for "manter", voltar para /focus (não há passo de intensidade)
      // Caso contrário, voltar para o passo 1 (intensidade)
      if (goal === 'manter') {
        navigate('/focus');
      } else {
        setStep(1);
      }
    } else if (step === 3) {
      // Passo 3 (Dados) -> Voltar para Nível de Movimento
      setStep(2);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="bg-primary text-white p-4 flex items-center gap-4">
        <button 
          onClick={handleBack}
          className="p-2 hover:bg-white/10 rounded-full transition-colors"
          data-testid="back-button"
        >
          <ArrowLeft size={24} weight="bold" />
        </button>
        <div>
          <h1 className="text-xl font-bold">Triagem</h1>
          <p className="text-sm text-white/80">{goalLabels[goal] || 'Configure seu perfil'}</p>
        </div>
      </header>

      {/* Progress bar */}
      <div className="h-1 bg-muted">
        <div 
          className="h-full bg-primary transition-all duration-300"
          style={{ width: `${(step / 3) * 100}%` }}
        />
      </div>

      {/* Content */}
      <main className="flex-1 p-6">
        <AnimatePresence mode="wait">
          {/* Step 1: Intensity */}
          {step === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="max-w-md mx-auto"
            >
              <h2 className="text-2xl font-bold text-foreground mb-6">Nível de Intensidade</h2>
              <p className="text-muted-foreground mb-6">Com que velocidade queres ver resultados?</p>
              <div className="space-y-4">
                {intensities.map(({ id, label, desc }) => (
                  <button
                    key={id}
                    onClick={() => handleIntensitySelect(id)}
                    className={`w-full p-4 rounded-2xl border-2 text-left transition-all ${
                      intensity === id 
                        ? "border-primary bg-primary/10" 
                        : "border-muted bg-white hover:border-primary/50"
                    }`}
                    data-testid={`intensity-${id}`}
                  >
                    <p className="font-bold text-foreground">{label}</p>
                    <p className="text-sm text-muted-foreground">{desc}</p>
                  </button>
                ))}
              </div>
            </motion.div>
          )}

          {/* Step 2: Activity Level */}
          {step === 2 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="max-w-md mx-auto"
            >
              <h2 className="text-2xl font-bold text-foreground mb-6">Nível de Movimento</h2>
              <p className="text-muted-foreground mb-6">Como é o teu dia-a-dia?</p>
              <div className="space-y-4">
                {activities.map(({ id, label, desc, icon: Icon }) => (
                  <button
                    key={id}
                    onClick={() => handleActivitySelect(id)}
                    className={`w-full p-4 rounded-2xl border-2 flex items-center gap-4 transition-all ${
                      activityLevel === id 
                        ? "border-primary bg-primary/10" 
                        : "border-muted bg-white hover:border-primary/50"
                    }`}
                    data-testid={`activity-${id}`}
                  >
                    <div className="w-12 h-12 bg-muted rounded-xl flex items-center justify-center">
                      <Icon size={28} className="text-primary" weight="duotone" />
                    </div>
                    <div className="text-left">
                      <p className="font-bold text-foreground">{label}</p>
                      <p className="text-sm text-muted-foreground">{desc}</p>
                    </div>
                  </button>
                ))}
              </div>
            </motion.div>
          )}

          {/* Step 3: Body Data */}
          {step === 3 && (
            <motion.div
              key="step3"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="max-w-md mx-auto"
            >
              <h2 className="text-2xl font-bold text-foreground mb-6">Os teus dados</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Género</label>
                  <div className="flex gap-4">
                    {[
                      { id: "feminino", label: "👩 Feminino" },
                      { id: "masculino", label: "👨 Masculino" }
                    ].map((g) => (
                      <button
                        key={g.id}
                        onClick={() => setGender(g.id)}
                        className={`flex-1 p-3 rounded-xl border-2 font-medium transition-all ${
                          gender === g.id 
                            ? "border-primary bg-primary/10 text-primary" 
                            : "border-muted bg-white text-foreground"
                        }`}
                        data-testid={`gender-${g.id}`}
                      >
                        {g.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Idade</label>
                  <input
                    type="number"
                    placeholder="Ex: 28"
                    value={age}
                    onChange={(e) => setAge(e.target.value)}
                    className="input-nzadi"
                    data-testid="age-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Altura (cm)</label>
                  <input
                    type="number"
                    placeholder="Ex: 165"
                    value={height}
                    onChange={(e) => setHeight(e.target.value)}
                    className="input-nzadi"
                    data-testid="height-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Peso atual (kg)</label>
                  <input
                    type="number"
                    step="0.1"
                    placeholder="Ex: 68.5"
                    value={weight}
                    onChange={(e) => setWeight(e.target.value)}
                    className="input-nzadi"
                    data-testid="weight-input"
                  />
                </div>

                {goal !== "manter" && (
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Peso objetivo (kg)
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      placeholder={goal === "perder" ? "Ex: 60" : "Ex: 75"}
                      value={targetWeight}
                      onChange={(e) => setTargetWeight(e.target.value)}
                      className="input-nzadi"
                      data-testid="target-weight-input"
                    />
                  </div>
                )}

                <button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="btn-primary w-full mt-6 disabled:opacity-50"
                  data-testid="submit-button"
                >
                  {loading ? "A calcular..." : "Criar o meu plano"}
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      {/* Warning Dialog */}
      <Dialog open={showWarning} onOpenChange={setShowWarning}>
        <DialogContent className="bg-white rounded-3xl border-none max-w-sm mx-auto">
          <DialogHeader>
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-zest/10 rounded-full flex items-center justify-center">
                <Warning size={40} className="text-zest" weight="duotone" />
              </div>
            </div>
            <DialogTitle className="text-center text-xl">Atenção!</DialogTitle>
            <DialogDescription className="text-center">
              Planos agressivos exigem rigor e saúde estável. Recomendamos consultar um profissional de saúde. Confirmas que queres prosseguir?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-3 sm:justify-center">
            <Button 
              variant="outline" 
              onClick={() => setShowWarning(false)}
              className="rounded-full"
              data-testid="cancel-aggressive"
            >
              Escolher outro
            </Button>
            <Button 
              onClick={confirmAggressive}
              className="btn-primary"
              data-testid="confirm-aggressive"
            >
              Confirmo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Footer */}
      <footer className="p-4 text-center text-muted-foreground text-sm">
        Nzadi Fit © 2026 – Saúde em Angola 🇦🇴
      </footer>
    </div>
  );
};

export default Onboarding;
